import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GridColDef } from '@mui/x-data-grid-pro'
import { useNewTranslation } from 'hooks/index'
import _ from 'lodash'
import {
    selectSelectedCampaignUuid,
    selectSelectedSubmissionView,
    selectSidebarTask,
    selectSidebarStep,
    selectSelectedCampaign,
    selectSearchablePlatforms,
    selectCheckCanReviewSubmissionByStep,
} from 'state/review'
import { SubmissionState, SUBMISSION_VIEW_ENUM, exportSubmissions } from 'state/submissions'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    getColumns as getScreenshotColumns,
    getRows as getScreenshotRows,
    RowsProps as SceenshotRowsProps,
} from './ScreenshotTableDef'
import {
    getColumns as getEngagementScreenshotColumns,
    getRows as getEngagementScreenshotRows,
    RowsProps as EngagementSceenshotRowsProps,
} from './EngagementScreenshotTableDef'
import { getColumns as getPostColumns, getRows as getPostRows, RowsProps as PostRowsProps } from './PostTableDef'
import { getColumns as getUrlColumns, getRows as getUrlRows, RowsProps as UrlRowsProps } from './UrlTableDef'
import {
    getColumns as getEngagementPostColumns,
    getRows as getEngagementPostRows,
    RowsProps as EngagementPostRowsProps,
} from './EngagementPostTableDef'
import {
    getColumns as getNoneSubmitColumns,
    getRows as getNoneSubmitRows,
    RowsProps as NoneSubmitTableRowsProps,
} from './NoneSubmitStepTableDef'
import * as CustomEngagementTableDef from './CustomEngagementTableDef'
import { RowsProps as CustomEngagementRowsProps } from './CustomEngagementTableDef'
import VerifyTable from '../VerifyTable'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { selectPartiposterProfiles } from 'state/partiposter'
import { TASK_TYPE_ENUM, TaskTypes, STEP_TYPE_ENUM } from 'services/api/constants'
import {
    selectFollowerTierOptions,
    selectCampaignTaskTypes,
    selectTaskPlatformContentTypesByName,
    selectIsEngagementStepTypeHelper,
} from 'state/constants'
import { EngagementId } from 'state/tasks'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { isNoneSubmitBlockExisted } from 'components/utils/submission'
import ExportModal from '../../common/ExportModal'
import { RequestExportSubmissionsPayload } from 'services/api/submissions'
import { HandleEditTagChange, HandleRejectTagChange } from 'components/utils/review'
import { TableViewType } from 'components/Shared/MuiTable'

interface ReviewTableProps {
    submissions: SubmissionState[]
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
    tableHeight: string
}

const ReviewTableWrapper: React.FC<ReviewTableProps> = ({
    tableHeight,
    submissions,
    handleEditTagChange,
    handleRejectTagChange,
}) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)
    const submissionLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.SubmissionsList]))
    const sidebarTask = useSelector(selectSidebarTask)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const selectedStep = useSelector(selectSidebarStep)
    const profiles = useSelector(selectPartiposterProfiles)
    const followerTierOptions = useSelector(selectFollowerTierOptions)
    const selectedCampaign = useSelector(selectSelectedCampaign)
    const taskTypes = useSelector(selectCampaignTaskTypes)
    const isSubmissionExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportSubmissions]))
    const taskSettings = _.keyBy(taskTypes, 'name')
    const selectedTaskSettings = sidebarTask && (taskSettings[sidebarTask.type] as TaskTypes)
    const platformContentTypesByName = useSelector(selectTaskPlatformContentTypesByName)
    const checkCanReviewSubmissionByStep = useSelector(selectCheckCanReviewSubmissionByStep)
    const canVerifySubmission = useMemo(
        () => (selectedStep ? checkCanReviewSubmissionByStep(selectedStep.type) : false),
        [checkCanReviewSubmissionByStep, selectedStep],
    )
    const canManageTag = usePermission(CampaignSubmission.ManageTag)
    const title =
        selectedSubmissionView === 'edits_requested'
            ? selectedStep
                ? selectedStep.data.title + ' - ' + newT('statusLabel.editRequest', 'Edits Requested')
                : ''
            : selectedStep
              ? selectedStep.data.title + ' - ' + newT('statusLabel.completed', 'Completed')
              : ''
    const searchablePlatforms = useSelector(selectSearchablePlatforms)

    const handleClickExport = (viewType: SUBMISSION_VIEW_ENUM | null) => {
        if (!selectedCampaignUuid || !selectedStep || !viewType) return
        return dispatch(
            exportSubmissions({
                uuid: selectedCampaignUuid,
                viewType: viewType as RequestExportSubmissionsPayload['viewType'],
                stepUuid: selectedStep.uuid,
            }),
        )
    }

    if (selectedCampaignUuid && sidebarTask && selectedCampaign) {
        const showLinkInBio =
            sidebarTask.data && (sidebarTask.data.link_in_bio_type == 1 || sidebarTask.data.link_in_bio_type == 2)

        if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING && canVerifySubmission) {
            return (
                <VerifyTable
                    task={sidebarTask}
                    submissions={submissions}
                    campaignUuid={selectedCampaignUuid}
                    showLinkInBio={showLinkInBio}
                    handleEditTagChange={handleEditTagChange}
                    handleRejectTagChange={handleRejectTagChange}
                />
            )
        }

        let columns: GridColDef[],
            rows:
                | UrlRowsProps[]
                | SceenshotRowsProps[]
                | EngagementSceenshotRowsProps[]
                | PostRowsProps[]
                | EngagementPostRowsProps[]
                | CustomEngagementRowsProps[]
                | NoneSubmitTableRowsProps[],
            rowHeight: number
        switch (sidebarTask.type) {
            case TASK_TYPE_ENUM.ATTEND_EVENT:
            case TASK_TYPE_ENUM.PURCHASE_PRODUCT:
                columns = getNoneSubmitColumns()
                rows = getNoneSubmitRows(submissions, profiles, selectedCampaign.country)
                rowHeight = 92
                return (
                    <>
                        <PartiTable
                            title={title}
                            tableHeight={tableHeight}
                            rowHeight={rowHeight}
                            getRowHeight={() => 'auto'}
                            columns={columns}
                            rows={rows}
                            isLoading={submissionLoading}
                            disableColumnMenu
                            hideToolbar={false}
                            showMoreAction
                            isNewStyle
                            showFilter
                            enableExport={!!rows.length}
                            viewType={TableViewType.SUBMISSIONS}
                        />

                        <ExportModal
                            tableTitle={title}
                            isRequesting={isSubmissionExportRequesting}
                            onClickExport={() => handleClickExport(selectedSubmissionView)}
                        />
                    </>
                )
            case TASK_TYPE_ENUM.IG_STORY:
            case TASK_TYPE_ENUM.IG_STORY_V02:
                if (selectedStep && selectedStep.type === STEP_TYPE_ENUM.POST_TO_IG_STORY) {
                    columns = getPostColumns()
                    rows = getPostRows(submissions, profiles, selectedCampaign.country)
                    rowHeight = 40
                } else {
                    const engagementTypesConfig = selectedTaskSettings && selectedTaskSettings.config.engagement_types
                    const engagementTypes = Object.keys(sidebarTask.payouts).filter(
                        payoutType => engagementTypesConfig && engagementTypesConfig.includes(payoutType),
                    ) as EngagementId[]
                    columns =
                        sidebarTask.type === TASK_TYPE_ENUM.IG_STORY
                            ? getScreenshotColumns(selectedSubmissionView, showLinkInBio, canManageTag)
                            : getEngagementScreenshotColumns(
                                  selectedSubmissionView,
                                  showLinkInBio,
                                  engagementTypes,
                                  canManageTag,
                              )
                    rows =
                        sidebarTask.type === TASK_TYPE_ENUM.IG_STORY
                            ? getScreenshotRows(
                                  selectedSubmissionView,
                                  selectedCampaignUuid,
                                  submissions,
                                  profiles,
                                  sidebarTask,
                                  followerTierOptions,
                                  selectedCampaign ? selectedCampaign.country : undefined,
                              )
                            : getEngagementScreenshotRows(
                                  selectedSubmissionView,
                                  selectedCampaignUuid,
                                  submissions,
                                  profiles,
                                  sidebarTask,
                                  selectedCampaign.country,
                              )
                    rowHeight = 114
                }
                return (
                    <>
                        <PartiTable
                            //exportData={rows} // todo: refactor this after api supoprt return all data
                            title={title}
                            rowHeight={rowHeight}
                            getRowHeight={() => 'auto'}
                            tableHeight={tableHeight}
                            columns={columns}
                            rows={rows}
                            isNewStyle
                            isLoading={submissionLoading}
                            hideToolbar={false}
                            showMoreAction
                            showFilter
                            enableExport={!!rows.length}
                            viewType={TableViewType.SUBMISSIONS}
                        />

                        <ExportModal
                            tableTitle={title}
                            isRequesting={isSubmissionExportRequesting}
                            onClickExport={() => handleClickExport(selectedSubmissionView)}
                        />
                    </>
                )
            case TASK_TYPE_ENUM.FB_POST:
            case TASK_TYPE_ENUM.FB_POST_V02:
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.IG_POST_V02:
            case TASK_TYPE_ENUM.TIKTOK_POST:
            case TASK_TYPE_ENUM.IG_REEL:
                if (
                    selectedStep &&
                    (selectedStep.type === STEP_TYPE_ENUM.POST_TO_IG_POST ||
                        selectedStep.type === STEP_TYPE_ENUM.POST_TO_FB_POST)
                ) {
                    columns = getPostColumns()
                    rows = getPostRows(submissions, profiles, selectedCampaign.country)
                    rowHeight = 40
                } else if (
                    selectedStep &&
                    (selectedStep.type === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST ||
                        selectedStep.type === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST ||
                        selectedStep.type === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST ||
                        selectedStep.type === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL)
                ) {
                    const engagementTypesConfig = selectedTaskSettings && selectedTaskSettings.config.engagement_types
                    const engagementTypes = Object.keys(sidebarTask.payouts).filter(
                        payoutType => engagementTypesConfig && engagementTypesConfig.includes(payoutType),
                    ) as EngagementId[]
                    columns = getEngagementPostColumns(
                        selectedSubmissionView,
                        showLinkInBio,
                        engagementTypes,
                        sidebarTask,
                        canManageTag,
                    )
                    rows = getEngagementPostRows(
                        selectedSubmissionView,
                        selectedCampaignUuid,
                        submissions,
                        profiles,
                        sidebarTask,
                        selectedCampaign.country,
                    )
                    rowHeight = 114
                } else {
                    columns = getUrlColumns(
                        selectedSubmissionView,
                        showLinkInBio,
                        selectedStep,
                        canManageTag,
                        sidebarTask,
                    )
                    rows = getUrlRows(
                        selectedSubmissionView,
                        selectedCampaignUuid,
                        submissions,
                        profiles,
                        sidebarTask,
                        selectedCampaign.country,
                    )
                    rowHeight = 84
                }
                return (
                    <>
                        <PartiTable
                            //exportData={rows} // todo: refactor this after api supoprt return all data
                            title={title}
                            rowHeight={rowHeight}
                            getRowHeight={() => 'auto'}
                            tableHeight={tableHeight}
                            columns={columns}
                            rows={rows}
                            isLoading={submissionLoading}
                            hideToolbar={false}
                            enableExport={!!rows.length}
                            viewType={TableViewType.SUBMISSIONS}
                            showFilter
                            isNewStyle
                            showMoreAction
                        />
                        <ExportModal
                            tableTitle={title}
                            isRequesting={isSubmissionExportRequesting}
                            onClickExport={() => handleClickExport(selectedSubmissionView)}
                        />
                    </>
                )
            case TASK_TYPE_ENUM.CUSTOM:
                let isNonSubmissionBlock = false
                if (selectedStep) {
                    isNonSubmissionBlock = isNoneSubmitBlockExisted(selectedStep)
                }
                if (isNonSubmissionBlock) {
                    columns = getNoneSubmitColumns()
                    rows = getNoneSubmitRows(submissions, profiles, selectedCampaign.country)
                    rowHeight = 56
                    return (
                        <>
                            <PartiTable
                                title={title}
                                rowHeight={rowHeight}
                                getRowHeight={() => 'auto'}
                                tableHeight={tableHeight}
                                columns={columns}
                                rows={rows}
                                isLoading={submissionLoading}
                                disableColumnMenu
                                isNewStyle
                                showMoreAction
                                hideToolbar={false}
                                enableExport={!!rows.length}
                                viewType={TableViewType.SUBMISSIONS}
                                showFilter
                            />
                            <ExportModal
                                tableTitle={title}
                                isRequesting={isSubmissionExportRequesting}
                                onClickExport={() => handleClickExport(selectedSubmissionView)}
                            />
                        </>
                    )
                } else if (
                    selectedStep &&
                    isEngagementStepType(selectedStep.type) &&
                    sidebarTask.platform_content_type &&
                    platformContentTypesByName[sidebarTask.platform_content_type]
                ) {
                    const engagementTypesConfig =
                        sidebarTask.platform_content_type &&
                        platformContentTypesByName[sidebarTask.platform_content_type].engagement_types.map(
                            item => item.id,
                        )
                    const engagementTypes = Object.keys(sidebarTask.payouts).filter(
                        payoutType =>
                            engagementTypesConfig && engagementTypesConfig.includes(payoutType as EngagementId),
                    ) as EngagementId[]
                    columns = CustomEngagementTableDef.getColumns(
                        selectedSubmissionView,
                        engagementTypes,
                        selectedStep.type,
                        searchablePlatforms,
                        canManageTag,
                    )
                    rows = CustomEngagementTableDef.getRows(
                        selectedSubmissionView,
                        selectedCampaignUuid,
                        submissions,
                        profiles,
                        selectedCampaign.country,
                    )
                    rowHeight = 114
                    return (
                        <>
                            <PartiTable
                                //exportData={rows} // todo: refactor this after api supoprt return all data
                                title={title}
                                rowHeight={rowHeight}
                                getRowHeight={() => 'auto'}
                                tableHeight={tableHeight}
                                columns={columns}
                                rows={rows}
                                isLoading={submissionLoading}
                                hideToolbar={false}
                                enableExport={!!rows.length}
                                viewType={TableViewType.SUBMISSIONS}
                                showFilter
                                isNewStyle
                                showMoreAction
                            />
                            <ExportModal
                                tableTitle={title}
                                isRequesting={isSubmissionExportRequesting}
                                onClickExport={() => handleClickExport(selectedSubmissionView)}
                            />
                        </>
                    )
                }
            default:
        }
    }
    return null
}

export default ReviewTableWrapper
